import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const services = [
  {
    icon: 'fa-solid fa-industry',
    title: '1. Industrial IoT (IIoT) & Factory Automation',
    desc: 'Connect plant floor machinery, edge gateways, and PLCs to capture real-time operational metrics, telemetry, and machine health data.',
  },
  {
    icon: 'fa-solid fa-screwdriver-wrench',
    title: '2. AI Predictive Maintenance',
    desc: 'Leverage machine learning models to analyze vibration, thermal, and performance telemetry to predict equipment failures before downtime occurs.',
  },
  {
    icon: 'fa-solid fa-cube',
    title: '3. Digital Twin & Virtual Factory Simulation',
    desc: 'Create real-time 3D digital replicas of production lines to simulate bottlenecks, test workflow configurations, and optimize plant output.',
  },
  {
    icon: 'fa-solid fa-microchip',
    title: '4. Manufacturing Execution Systems (MES)',
    desc: 'Integrate advanced MES solutions to automate production scheduling, track work-in-progress (WIP), and eliminate paper-based floor logs.',
  },
  {
    icon: 'fa-solid fa-eye',
    title: '5. Computer Vision Quality Inspection',
    desc: 'Deploy high-speed automated camera systems using deep learning to detect surface defects, dimension mismatches, and packaging flaws in real time.',
  },
  {
    icon: 'fa-solid fa-truck-ramp-box',
    title: '6. Smart Supply Chain & Inventory Tracking',
    desc: 'Implement RFID, BLE, and GPS tracking to gain end-to-end visibility across raw material procurement, warehouse inventory, and logistics.',
  },
  {
    icon: 'fa-solid fa-gauge-high',
    title: '7. Overall Equipment Effectiveness (OEE) Analytics',
    desc: 'Monitor real-time OEE metrics—Availability, Performance, and Quality—via dynamic executive dashboards to maximize plant throughput.',
  },
  {
    icon: 'fa-solid fa-bolt',
    title: '8. Energy & Resource Management Systems',
    desc: 'Track plant-wide power consumption, water usage, and carbon emissions to meet regulatory compliance and reduce operational utility costs.',
  },
  {
    icon: 'fa-solid fa-robot',
    title: '9. Collaborative Robotics & Warehouse AGVs',
    desc: 'Integrate autonomous mobile robots (AMRs) and cobots into assembly lines to handle repetitive material transport and precise tasks safely.',
  },
  {
    icon: 'fa-solid fa-shield-cat',
    title: '10. OT Cybersecurity & Industrial Compliance',
    desc: 'Protect operational technology (OT) networks with air-gapped security frameworks, threat monitoring, and ISA/IEC 62443 compliance standards.',
  },
];

const whyChooseUs = [
  'Deep expertise across Industry 4.0, IIoT architectures, and edge computing',
  'Seamless integration with legacy ERPs (SAP, Oracle) and modern MES platforms',
  'Real-time Computer Vision QA systems with 99%+ defect detection accuracy',
  'Reduced unplanned equipment downtime through AI-driven predictive analytics',
  'End-to-end OT/IT convergence with robust cybersecurity governance',
  'ISO 9001 and ISA/IEC 62443 compliant software development processes',
  'Scalable data pipelines handling multi-plant sensor streaming',
  'Customized Digital Twin simulations reducing commissioning lead times',
  'Measurable increase in Overall Equipment Effectiveness (OEE)',
  'Hands-on support from PoC prototype to enterprise plant floor deployment',
];

const processSteps = [
  { step: '01', title: 'Plant Assessment & Telemetry Audit', desc: 'Evaluate legacy machinery, sensor connectivity, network protocols, and high-value plant floor friction points.' },
  { step: '02', title: 'IIoT & Edge Architecture Design', desc: 'Design secure OT/IT network integration, edge computing nodes, and cloud telemetry ingestion pipelines.' },
  { step: '03', title: 'Sensor Integration & Data Capture', desc: 'Connect PLCs, SCADA systems, and IIoT sensors to capture continuous operational data streams.' },
  { step: '04', title: 'AI Model & MES Development', desc: 'Train computer vision defect detectors, build predictive maintenance models, and configure MES workflows.' },
  { step: '05', title: 'Pilot Line Deployment & Testing', desc: 'Deploy solutions on a target production line, conduct stress tests, and validate OEE improvements.' },
  { step: '06', title: 'Enterprise Rollout & Continuous Optimization', desc: 'Scale deployment across all plant facilities, optimize model accuracy, and deliver continuous support.' },
];

const techStack = [
  { category: 'IIoT & Edge Gateways', items: ['AWS IoT Core', 'Azure IoT Hub', 'Siemens MindSphere', 'MQTT', 'OPC UA'] },
  { category: 'AI & Vision Frameworks', items: ['PyTorch', 'TensorFlow', 'OpenCV', 'YOLO', 'Hugging Face'] },
  { category: 'PLCs & OT Protocols', items: ['Siemens S7', 'Modbus', 'PROFINET', 'Allen-Bradley', 'Rockwell Automation'] },
  { category: 'Analytics & Platforms', items: ['ThingWorx', 'Power BI', 'Grafana', 'InfluxDB', 'Apache Kafka'] },
];

// Animation Variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const ManufacturingSolutions = () => {
  return (
    <div className="service-page text-white overflow-hidden">
      {/* 1. HERO SECTION */}
      <section className="hero-sub-section text-center d-flex align-items-center justify-content-center">
        <div className="container py-5">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="badge rounded-pill bg-outline-cyan px-3 py-2 mb-3 glow-badge">
              Lidlbritain Industrial Innovation
            </span>
            <h1 className="display-4 fw-bold mb-3">
              Smart Manufacturing <span className="gradient-text-cyan">Solutions</span>
            </h1>
            <p className="lead mx-auto opacity-75 max-width-800">
              At <strong>Lidlbritain</strong>, we bridge the gap between Operational Technology (OT) and Information Technology (IT). From IIoT edge integration to computer vision quality control and predictive maintenance, we build high-efficiency digital factories.
            </p>
            <div className="mt-4">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="d-inline-block">
                <Link to="/contact" className="btn gradient-btn px-4 py-2 rounded-pill">
                  Transform Your Plant Operations &rarr;
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 2. SERVICES GRID SECTION */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold">
              Manufacturing Solutions by <span className="gradient-text-orange">Lidlbritain</span>
            </h2>
            <div className="title-line mx-auto mt-2"></div>
          </div>

          <motion.div
            className="row g-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
          >
            {services.map((item, index) => (
              <div key={index} className="col-md-6 col-lg-4">
                <motion.div
                  variants={cardVariants}
                  whileHover={{ y: -8, transition: { duration: 0.2 } }}
                  className="service-card p-4 rounded-4 h-100 border border-secondary border-opacity-25 bg-glass"
                >
                  <div className="icon-box mb-3 d-flex align-items-center justify-content-center floating-icon">
                    <i className={`${item.icon} fs-3 text-cyan`}></i>
                  </div>
                  <h5 className="fw-bold mb-3">{item.title}</h5>
                  <p className="text-light opacity-75 small leading-relaxed">{item.desc}</p>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 3. WHY CHOOSE US & TECH STACK SECTION */}
      <section className="py-5 bg-dark-overlay">
        <div className="container">
          <div className="row g-5 align-items-center">
            {/* Why Choose Lidlbritain */}
            <motion.div
              className="col-lg-6"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="fw-bold mb-4">
                Why Partner With <span className="gradient-text-cyan">Lidlbritain?</span>
              </h3>
              <div className="row g-3">
                {whyChooseUs.map((reason, idx) => (
                  <div key={idx} className="col-sm-6">
                    <div className="d-flex align-items-center gap-2">
                      <i className="fa-solid fa-circle-check text-orange pulse-icon"></i>
                      <span className="small opacity-90">{reason}</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Tools & Tech Stack */}
            <motion.div
              className="col-lg-6"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="p-4 rounded-4 border border-secondary border-opacity-25 bg-glass glow-box">
                <h4 className="fw-bold mb-4">Smart Manufacturing Tech Ecosystem</h4>
                {techStack.map((tech, idx) => (
                  <div key={idx} className="mb-3">
                    <h6 className="text-cyan mb-2 fw-semibold">{tech.category}</h6>
                    <div className="d-flex flex-wrap gap-2">
                      {tech.items.map((item, i) => (
                        <motion.span
                          whileHover={{ scale: 1.1 }}
                          key={i}
                          className="badge bg-secondary bg-opacity-22 border border-secondary border-opacity-50 text-light fw-normal py-2 px-3"
                        >
                          {item}
                        </motion.span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 4. PROCESS SECTION */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold">
              The Lidlbritain <span className="gradient-text-orange">Industry 4.0 Framework</span>
            </h2>
            <div className="title-line mx-auto mt-2"></div>
          </div>

          <motion.div
            className="row g-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {processSteps.map((stepItem, index) => (
              <div key={index} className="col-md-6 col-lg-4">
                <motion.div
                  variants={cardVariants}
                  whileHover={{ scale: 1.03 }}
                  className="process-card p-4 rounded-4 h-100 bg-glass border border-secondary border-opacity-25 position-relative"
                >
                  <span className="step-number fw-bold text-cyan mb-2 d-block">{stepItem.step}</span>
                  <h5 className="fw-bold mb-2">{stepItem.title}</h5>
                  <p className="text-light opacity-75 small mb-0">{stepItem.desc}</p>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 5. CALL TO ACTION SECTION */}
      <section className="py-5 my-4">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="p-5 rounded-5 cta-banner text-center position-relative overflow-hidden"
          >
            <h2 className="fw-bold mb-3">Build an Intelligent Smart Factory with Lidlbritain</h2>
            <p className="lead opacity-90 mx-auto mb-4 max-width-800">
              Eliminate unplanned downtime, optimize production yield, and gain real-time visibility across all your manufacturing lines.
            </p>
            <motion.div whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.95 }} className="d-inline-block">
              <Link to="/contact" className="btn gradient-btn btn-lg px-5 py-3 rounded-pill fw-semibold">
                Consult with a Manufacturing Specialist &rarr;
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ManufacturingSolutions;